import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertComponent,
  BreadcrumbComponent,
} from "../../../../common/components";
import { MUITable } from "../../../../common/components";
import {
  getRules,
  deleteRule,
  runTheRule,
} from "../../../../api/lead-assignment";
import { PaginationComponent } from "../../../../common/components";
import { useSetLoading } from "../../../../common/hooks/appearance";
import "../lead-assignment.scss";
import _ from "lodash";
import LeadAssignmentRulesFilters from "./lead-assignment-rules-filters";
import { Link, useLocation } from "react-router-dom";
import { routes } from "../../../../constants/routes";
import EditRule from "./edit-rule";
import { Rule } from "../lead-assignment-props";
import ConfirmationPopup from "../../../../common/components/confirmation-popup/confirmation-popup";
import AssignFilterOptions from "./assign-filter-options/assign-filter-options";
import { assignfilterOptions } from "./assign-filter-options/assign-filter-options-constant";
import { Box } from "@mui/material";
import moment from "moment";
import StyledTooltip from "../../../../common/components/mui-table/styled-tooltip";
import { useResourcePermissions } from "../../../../common/hooks/resource-permissions";
import MODULES from "../../../../constants/modules";
import { convertUTCToLocal } from "../../../../utils/utils";

interface SortOption {
  sortDirection: string | null;
  sortBy: string | null;
}

const AssignmentRules = () => {
  const breadcrumbLinkForList = [
    { label: "Lead Assignment", link: routes.ASSIGNMENTRULES, handleClick: () => {} },
    { label: "Assignment Rules", link: "#", handleClick: () => {} },
  ];
  const [breadcrumbLink, setBreadcrumbLink] = useState(breadcrumbLinkForList);
  const { canWrite, canDelete, canRead } = useResourcePermissions(
    MODULES.LEAD_ASSIGNMENT
  );

  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedLeadsMap, setSelectedLeadsMap] = useState<{
    [page: number]: number[];
  }>({});
  const isAllSelected = selectedLeadsMap[currentPage]?.length === data?.length;
  const isIndeterminate =
    selectedLeadsMap[currentPage]?.length > 0 &&
    selectedLeadsMap[currentPage]?.length < data?.length;
  const [selectedLeadsFilterOptions, setSelectedLeadsFilterOptions] =
    useState<any[]>(assignfilterOptions);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageSize] = useState<number>(15);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [error, setError] = useState("");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [sortOption, setSortOption] = useState<SortOption>({
    sortDirection: null,
    sortBy: null,
  });
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [selectedLeadsFilter, setSelectedLeadsFilters] = useState<number[]>([]);
  const [searchText, setSearchText] = useState<any>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [ruleId, setRuleId] = useState<number>(0);
  const [currentRule, setCurrentRule] = useState<Rule>();
  const [isDelete, setIsDelete] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [isResetSearch, setIsResetSearch] = useState(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const isSearchTriggered = useRef(false);
  const [totalDeletedRules, setTotalDeletedRules] = useState(0);
  const [isRun, setIsRun] = useState(false);
  const [runClickedIds, setRunClickedIds] = useState<Set<number>>(new Set());

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1); // Reset to page 1 on search
      setSearchText(text);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    []
  );

  const resetSearch = () => {
    setSearchText("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
  };

  useEffect(() => {
    const pageToUse = isSearchTriggered.current ? 1 : currentPage;
    fetchRules(pageToUse, searchText || "", activeFilters);
    isSearchTriggered.current = false;
  }, [currentPage, sortOption, searchText, activeFilters]);

  // useEffect(() => {
  //   //to get the total delete rules
  //   fetchRules(1, "", {}, true);
  // },[])

  const noResultsTxt = useMemo(() => {
    if (loading) {
      return "Loading Assignment Rules...";
    }
    if (searchText) {
      return "No Assignment Rules matching your search criteria.";
    }
    return "No Assignment Rule found";
  }, [loading, searchText]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };
  const location = useLocation();

  useEffect(() => {
    if (!isEdit) {
      setBreadcrumbLink(breadcrumbLinkForList);
    }
  }, [isEdit]);

  useEffect(() => {
    if (location.state?.fromEditRule) {
      setIsEdit(false);
    }
    if (location.state?.message) {
      setAlertMessage(location.state.message);
      setAlertType(location.state.type);
      setAlertOpen(true);
    }
    window.history.replaceState({}, document.title);
  }, [location.state]);

  const fetchRules = async (
    page = 1,
    search: string = "",
    filters: any = {},
    deletedRules: boolean = false
  ) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      let params: any = {
        limit: pageSize,
        offset: offset,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      if (filters?.status) {
        params.status = filters.status;
      }
      if (filters?.createdTimeStart && filters.createdTimeEnd) {
        if (filters.createdTimeStart.isSame(filters.createdTimeEnd)) {
          params.createdAtStart = filters.createdTimeStart.startOf("day");
          params.createdAtEnd = filters.createdTimeEnd.endOf("day");
        } else {
          params.createdAtStart = filters.createdTimeStart;
          params.createdAtEnd = filters.createdTimeEnd;
        }
      }
      if (filters?.lastRunStart && filters.lastRunEnd) {
        if (filters.lastRunStart.isSame(filters.lastRunEnd)) {
          params.lastRunStart = filters.lastRunStart.startOf("day");
          params.lastRunEnd = filters.lastRunEnd.endOf("day");
        } else {
          params.lastRunStart = filters.lastRunStart;
          params.lastRunEnd = filters.lastRunEnd;
        }
      }
      if (filters && filters.address && Object.keys(filters.address).length > 0) {
        const { country, state, city, postcode } = filters.address;
        if (country) params.country = country;
        if (state) params.state = state;
        if (city) params.city = city;
        if (postcode) params.postcode = postcode;
      }
      if (sortOption?.sortBy) {
        params = { ...params, ...sortOption };
      }
      if (search) {
        params.searchText = search;
      }
      const response = await getRules(params);
      setData(response.data.data.result);
      setTotalRecords(response.data.data.totalRecords);
      setTotalDeletedRules(response.data.data.totalDeletedRecords || 0);
      setTimeout(() => setLoading(false), 1000);
    } catch (error) {
      setError("Failed to fetch leads list");
      setLoading(false);
    }
  };

  const deleteRuleAPI = async () => {
    setLoading(true);
    try {
      const response = await deleteRule(ruleId);
      if (response?.status === 204) {
        setAlertMessage(`Assignment rule archived successfully.`);
        setAlertType("success");
        setSelectedLeadsMap({});
        if (searchText) resetSearch();
        if (currentPage === 1) {
          fetchRules();
        } else setCurrentPage(1);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while archiving the rule."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
      setAlertOpen(true);
      handleDeleteClose();
    }
  };

  const runRuleAPI = async (id: number) => {
    setLoading(true);
    try {
      const response = await runTheRule(id);
      if (response?.status === 200) {
        if (
          response?.data?.data?.jobAdded === false &&
          response?.data?.data?.message ===
            "No leads Found for this rule with the matching criteria."
        ) {
          setAlertMessage(
            `No approved leads Found for this rule with the matching criteria.`
          );
          setAlertType("warning");
          setRunClickedIds((prevIds) => {
            const newIds = new Set(prevIds);
            newIds.delete(id);
            return newIds;
          });
        }
        else if (
          response?.data?.data?.jobAdded === false &&
          (response?.data?.data?.message === "No active partners Found for this rule with the matching criteria." || 
           response?.data?.data?.message === 'No partners Found for this rule with the matching criteria.')
        ) {
          setAlertMessage(
            response?.data?.data?.message
          );
          setAlertType("warning");
          setRunClickedIds((prevIds) => {
            const newIds = new Set(prevIds);
            newIds.delete(id);
            return newIds;
          });
        } else {
          setAlertMessage(
            `Running Assignment logic, Please wait while we process the leads.`
          );
          setAlertType("warning");
        }
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while running the rule."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
      setAlertOpen(true);
    }
  };

  const mapColumnNameToField = (columnName: string): string => {
    const mapping: Record<string, string> = {
      logicTitle: "rule_name",
      createdAt: "created_at",
      status: "is_active",
      leads: "leads",
      lastRun: "last_run_at",
    };

    return mapping[columnName] || columnName;
  };

  const handleSelectAll = () => {
    setSelectedLeadsMap((prev) => {
      const currentPageLeads = data.map((item: any) => item.id);
      const selectedLeadsOnPage = prev[currentPage] || [];

      if (selectedLeadsOnPage.length > 0) {
        return { ...prev, [currentPage]: [] };
      }
      return { ...prev, [currentPage]: currentPageLeads };
    });
  };
  const headers = [
    {
      key: "action",
      label: "Action",
      classNameGetter: (row: any) => {
        if (
          row?.LeadAssignmentTracker?.length >= 0 &&
          runClickedIds?.has(row?.id)
        ) {
          return "pending-rule-wrapper";
        }
        return "";
      },
    },
    { key: "logicTitle", label: "Logic Title", isOnClick: true },
    { key: "dateCreated", label: "Date Created" },
    { key: "status", label: "Status" },
    { key: "leads", label: "Leads" },
    { key: "lastRun", label: "Last Run" },
  ];

  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item: any) => {
        return {
          id: item.id,
          logicTitle: item.rule_name || "-",
          dateCreated: item.created_at
            ? convertUTCToLocal(item?.created_at, "MM/DD/YY")
            : "-",
          status: item.is_active ? "Active" : "Inactive",
          leads: (item._count?.assignedLeads || item?.assigned_leads_count) ?? 0,
          lastRun: item.last_run_at
            ? convertUTCToLocal(item.last_run_at, "MM-DD-YYYY hh:mm A")
            : "-",
          LeadAssignmentTracker: item?.LeadAssignmentTracker,
        };
      });
    }
    return [];
  }, [data]);

  //remove id from runClickedIds if the job is completed for that rule
  useEffect(() => {
    if (data?.length) {
      setRunClickedIds((prevIds) => {
        const newIds = new Set(prevIds);
        data.forEach((item) => {
          if (
            newIds?.has(item?.id) &&
            item.LeadAssignmentTracker?.length === 0
          ) {
            newIds.delete(item?.id);
          }
        });
        return newIds;
      });
    }
  }, [data]);

  const handleEdit = (id: number) => {
    setIsEdit(true);
  };
  const handleDeleteClose = () => {
    setIsDelete(false);
  };

  const setSingleRuleData = (id: number) => {
    const selectedRule = data?.find((rule) => rule.id === id);
    if (selectedRule) {
      setCurrentRule({
        id: selectedRule?.id,
        ruleName: selectedRule?.rule_name,
        geographyParam: selectedRule?.rule_json?.geography_param,
        geographyValue: selectedRule?.rule_json?.geography_value,
        assignedLead: selectedRule?._count?.assignedLeads,
        isActive: selectedRule?.is_active ? "Active" : "Inactive",
        distributionMethod: selectedRule?.distribution_method,
        productType: selectedRule?.rule_json?.product_type,
      });
    }
  };

  const handleRunningRule = (id: number) => {
    runRuleAPI(id);
  };

  const handleBackArrowClick = () => {
    setIsEdit(false);
    setBreadcrumbLink(breadcrumbLinkForList);
  }

  const handleAction = (actionType: string, id: number) => {
    setSingleRuleData(id);
    setRuleId(id);
    if (actionType === "Edit") {
      handleEdit(id);
      setBreadcrumbLink([
        {
          label: "Lead Assignment",
          link: routes.ASSIGNMENTRULES,
          handleClick: handleBackArrowClick,},
        {
          label: "Assignment Rules",
          link: routes.ASSIGNMENTRULES,
          handleClick: handleBackArrowClick
        },
        { label: "Edit Rule", link: "#", handleClick: () => {} },
      ]);
    } else if (actionType === "Archive") {
      setIsDelete(true);
    } else if (actionType === "Run") {
      setIsRun(true);
      handleRunningRule(id);
    }
  };

  const handleViewBreadcrumb = () => { 
    setIsViewOpen(false);
    setBreadcrumbLink(breadcrumbLinkForList);
  }
  const handleViewRule = (row: any) => {
    setRuleId(row?.id);
    setBreadcrumbLink([
      {
        label: "Lead Assignment",
        link: routes.ASSIGNMENTRULES,
        handleClick: handleViewBreadcrumb
      },
      {
        label: "Assignment Rules",
        link: routes.ASSIGNMENTRULES,
        handleClick: handleViewBreadcrumb
      },
      { label: "View Rule", link: "#", handleClick: () => {} },
    ]);
    setSingleRuleData(row?.id);
    setIsViewOpen(true);
  };
  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateFilterActive =
      activeFilters.createdTimeStart || activeFilters.createdTimeEnd;
    const runFilterActive =
      activeFilters.lastRunStart || activeFilters.lastRunEnd;

    const otherFilters = { ...activeFilters };
    delete otherFilters.createdTimeStart;
    delete otherFilters.createdTimeEnd;
    delete otherFilters.lastRunStart;
    delete otherFilters.lastRunEnd;

    const otherActiveFilters = Object.values(otherFilters).filter(
      (val) =>
        val !== undefined &&
        val !== "" &&
        val !== null &&
        !(Array.isArray(val) && val.length === 0)
    ).length;

    if (dateFilterActive) {
      count += 1;
    }
    if (runFilterActive) {
      count += 1;
    }

    count += otherActiveFilters;

    return count;
  }, [activeFilters]);

  const fetchRulesIncludingSearchAndFilters = () => {
    if (currentPage === 1) {
      const pageToUse = isSearchTriggered.current ? 1 : currentPage;
      fetchRules(pageToUse, searchText || "", activeFilters);
      isSearchTriggered.current = false;
    } else {
      setCurrentPage(1);
    }
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <div className="leadlist-wrapper lead-assignment-rules-wrapper">
        <div className="breadcrumb-wrapper">
          <BreadcrumbComponent breadcrumbLink={breadcrumbLink} />
        </div>
        <div style={{ display: !(isEdit || isViewOpen) ? "block" : "none" }}>
          <>
            {" "}
            <LeadAssignmentRulesFilters
              isOpenFilter={isOpenFilter}
              setIsOpenFilter={setIsOpenFilter}
              onSearch={handleSearch}
              selectedLeadsFilter={appliedFilterCount}
              totalDeletedRules={totalDeletedRules}
              searchRef={searchRef}
              fetchRules={fetchRulesIncludingSearchAndFilters}
            />
            <Box className="pending-wrapper">
              <Box className="rule-run-pending-wrapper">
                <Box className="payment-dot" />
                <span>Running rule is pending</span>
              </Box>
            </Box>
            <div className="lead-list-table assignment-rules-list-table">
              <div className="table-and-filters-wrapper">
                <MUITable
                  headers={headers}
                  data={formattedData?.map((item: any) => ({
                    id: item?.id,
                    action: "verticalEllipse",
                    logicTitle: item.logicTitle,
                    dateCreated: item.dateCreated,
                    status: item?.status,
                    leads:
                      item?.leads > 0 ? (
                        <Link
                          to={routes.ASSIGNMENTRULESLEADS}
                          state={{ id: item?.id }}
                          className="leads-link"
                        >
                          {item.leads}
                        </Link>
                      ) : (
                        <span className="leads-link">0</span>
                      ),
                    lastRun: item.lastRun,
                    leadCount: item?.leads,
                    disableRun: item?.status !== "Active" ,
                    LeadAssignmentTracker: item?.LeadAssignmentTracker,
                  }))}
                  noResultsTxt={noResultsTxt}
                  handleAction={handleAction}
                  onColumnClick={(row) => {
                    handleViewRule(row);
                  }}
                  runClickedIds={runClickedIds}
                  setRunClickedIds={setRunClickedIds}
                  fieldName={"assignment-rules"}
                  permissions={{
                    canDelete,
                    canRead,
                    canWrite,
                  }}
                />
                <ConfirmationPopup
                  open={isDelete}
                  buttonText="Archive"
                  onCancel={handleDeleteClose}
                  onOkay={deleteRuleAPI}
                  title={"Archive Assignment Rule"}
                  note={
                    "Are you sure you want to archive this assignment logic? This action cannot be undone."
                  }
                />
                <div
                  style={{ display: isOpenFilter ? "block" : "none" }}
                  className="lead-list-filter"
                >
                  <AssignFilterOptions
                    isOpenFilter={isOpenFilter}
                    setIsOpenFilter={setIsOpenFilter}
                    selectedLeadsFilter={selectedLeadsFilter}
                    setSelectedLeadsFilters={setSelectedLeadsFilters}
                    selectedLeadsFilterOptions={selectedLeadsFilterOptions}
                    setAlertMessage={setAlertMessage}
                    setAlertType={setAlertType}
                    setAlertOpen={setAlertOpen}
                    setSelectedLeadsFilterOptions={
                      setSelectedLeadsFilterOptions
                    }
                    onApplyFilters={(filters: any) => {
                      setActiveFilters(filters);
                      setCurrentPage(1);
                    }}
                    activeFilters={activeFilters}
                  />
                </div>
              </div>
            </div>
            {totalRecords && totalRecords > 0 ? (
              <div className="leads-pagination-container">
                <PaginationComponent
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            ) : (
              <></>
            )}
          </>
        </div>
        <div style={{ display: isEdit || isViewOpen ? "block" : "none" }}>
          <EditRule
            currentRule={currentRule}
            setIsEdit={setIsEdit}
            fetchRules={() => {
              if (searchText) resetSearch();
              if (currentPage === 1) fetchRules();
              else setCurrentPage(1);
            }}
            viewRule={isViewOpen}
          />
        </div>
      </div>
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
};

export default AssignmentRules;
